import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field, Icon } from '@barev/ui';
import { tOptional, errorText, haptic, useI18n, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { levelDiscount, PageHead, toneClass, type Level } from '../lib.tsx';

/** Услуга без онлайн-записи: «перезвоните мне» с комментарием — оператор оформит заказ сам */
export function ServiceRequest({ code }: { code: string }) {
  const { t } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const catalog = useQuery(trpc.client.catalog.queryOptions());
  const open = useQuery(trpc.client.requests.open.queryOptions());
  const [comment, setComment] = useState('');
  const create = useMutation(
    trpc.client.requests.create.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        void qc.invalidateQueries({ queryKey: trpc.client.requests.open.queryKey() });
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  const category = catalog.data?.categories.find((c) => c.services.some((s) => s.code === code));
  const level = (catalog.data?.level ?? 'BAREV') as Level;
  const pct = category ? levelDiscount(level, category) : 0;
  const sent = create.data ?? open.data?.find((r) => r.kind === 'SERVICE' && r.serviceCode === code);

  return (
    <main className="app app--plain">
      <PageHead title={t(`services.${code}.name`)} />
      <Card className={`bc-stack ${category ? toneClass(category.code) : ''}`}>
        <p style={{ margin: 0 }}>{tOptional(t, `services.${code}.description`)}</p>
        {pct > 0 && <div style={{ fontWeight: 700 }}>{t('client.request.yourDiscount', { pct })}</div>}
      </Card>
      {sent ? (
        <Card className="bc-stack" style={{ alignItems: 'center', textAlign: 'center' }}>
          <span className="req-done" aria-hidden="true">
            <Icon name="check" size={28} strokeWidth={2.6} />
          </span>
          <h2 className="bc-h3">{t('client.request.sentTitle', { number: sent.number })}</h2>
          <p className="bc-muted" style={{ margin: 0 }}>
            {t('client.request.sentText')}
          </p>
          <a href="#/services" className="bc-btn bc-btn--light">
            {t('client.request.back')}
          </a>
        </Card>
      ) : (
        <Card className="bc-stack">
          <h2 className="bc-h3">{t('client.request.title')}</h2>
          <p className="bc-small bc-muted" style={{ margin: 0 }}>
            {t('client.request.sub')}
          </p>
          <Field label={t('client.request.comment')}>
            <textarea className="bc-input" style={{ minHeight: 100 }} maxLength={1000} value={comment} placeholder={t('client.request.commentHint')} onChange={(e) => setComment(e.target.value)} />
          </Field>
          <Button onClick={() => create.mutate({ kind: 'SERVICE', serviceCode: code, comment: comment.trim() || undefined })} disabled={create.isPending}>
            {t('client.request.submit')}
          </Button>
        </Card>
      )}
    </main>
  );
}
